/**
 * The client's side of the API: the shapes the screens read, and the one
 * write they make.
 *
 * Every function here is a thin, typed `fetch` and nothing more. It does no
 * caching, no retrying and no merging — that is TanStack Query's job in
 * `src/queries.ts` — and it does no arithmetic, which is `src/lib/money.ts`'s.
 * What it does own is the refusal: a named error from the API comes back as an
 * `ApiFailure` carrying the code, so a screen can say "you are not in this
 * group any more" rather than "HTTP 403".
 *
 * The session cookie rides along on its own. These are same-origin requests,
 * which `fetch` sends credentials with by default.
 */

import type { MotionPreference } from './lib/motion'
import type { Split, UserId } from './lib/money'

/** A refusal the API was willing to name, as an exception a query can catch. */
export class ApiFailure extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiFailure'
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const res = await fetchImpl(path, init)

  let body: unknown
  try {
    body = await res.json()
  } catch {
    throw new ApiFailure('unreadable_response', `${path} did not answer with JSON`, res.status)
  }

  const shape = body as { status?: string; error?: string; message?: string }
  if (!res.ok || shape?.status !== 'ok') {
    throw new ApiFailure(
      shape?.error ?? 'request_failed',
      shape?.message ?? shape?.error ?? `HTTP ${res.status}`,
      res.status,
    )
  }
  return body as T
}

// ---------------------------------------------------------------------------
// The shapes, as the server sends them
// ---------------------------------------------------------------------------

export type PersonGroupShare = {
  groupId: string
  groupName: string
  /** Positive: they owe you, in this group. */
  amountCents: number
}

export type Person = {
  userId: UserId
  displayName: string
  /** Positive: they owe you, netted across every group you share. */
  amountCents: number
  groups: PersonGroupShare[]
}

export type BalanceTotals = {
  owedToYouCents: number
  youOweCents: number
  netCents: number
  people: number
}

export type BalancesResponse = {
  status: 'ok'
  people: Person[]
  /** Over every counterparty, not this page's — so it comes with the first page only. */
  totals: BalanceTotals | null
  nextCursor: string | null
}

export type GroupSummary = {
  id: string
  name: string
  currency: string
  /**
   * Whether this group's settle-up screen offers the simplified plan. On by
   * default, per group rather than per person — two people in the same group
   * being told to make different payments is how a plan stops being followed.
   */
  simplifyDebts: boolean
  archivedAt: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  memberCount: number
  you: { role: string; joinedAt: string; leftAt: string | null }
}

export type GroupsResponse = {
  status: 'ok'
  groups: GroupSummary[]
  nextCursor: string | null
}

export type Member = {
  userId: UserId
  displayName: string
  role: string
  joinedAt: string
  leftAt: string | null
  /**
   * Somebody in the group who has never signed in: a name a member typed, in
   * the ledger from the first expense, waiting on an invite link. Every screen
   * treats them as an ordinary member — they are one — except that they are
   * the only person there is anything to *send*.
   */
  isPlaceholder: boolean
}

export type GroupResponse = {
  status: 'ok'
  group: Omit<GroupSummary, 'memberCount' | 'you'>
  you: { role: string; joinedAt: string }
  members: Member[]
}

/**
 * What `POST /api/groups` answers with. It is `GroupResponse` without the
 * `you`, and that is the server's shape rather than an omission worth
 * patching: the one member in `members` is you, so there is nothing a `you`
 * could say that the list does not.
 */
export type CreatedGroupResponse = {
  status: 'ok'
  group: Omit<GroupSummary, 'memberCount' | 'you'>
  members: Member[]
}

export type MemberBalance = {
  userId: UserId
  displayName: string
  /** Positive: the group owes this person. */
  amountCents: number
  left: boolean
}

/** A group as every response that is not *about* the group states it. */
export type GroupRef = {
  id: string
  name: string
  currency: string
  simplifyDebts: boolean
  archivedAt: string | null
}

export type GroupBalancesResponse = {
  status: 'ok'
  group: GroupRef
  balances: MemberBalance[]
  nextCursor: string | null
}

export type Payment = {
  fromUserId: UserId
  fromDisplayName: string | null
  toUserId: UserId
  toDisplayName: string | null
  amountCents: number
}

/**
 * The before and the after, both of them, whichever list came back.
 *
 * `directCount` is what the ledger asks for as it stands — everybody pays the
 * people they actually owe — and `simplifiedCount` is what it takes once the
 * cycles are netted out. Both are sent on every plan, because the sentence a
 * settle-up screen owes somebody is "three payments instead of six": rerouting
 * a payment to a person they never transacted with is exactly the thing that
 * makes an app look like it is making things up, and it is only reassuring
 * when the screen says it is doing it.
 */
export type PlanSummary = {
  /** Whether `payments` went through `simplify`. */
  simplified: boolean
  /** Payments in the whole plan, not in this page of it. */
  paymentCount: number
  totalCents: number
  directCount: number
  simplifiedCount: number
}

export type SettlementPlanResponse = {
  status: 'ok'
  group: GroupRef
  payments: Payment[]
  summary: PlanSummary
  nextCursor: string | null
}

/** One payment somebody has recorded and is waiting for you to acknowledge. */
export type AwaitingSettlement = {
  transactionId: string
  amountCents: number
  currency: string
  description: string
  occurredOn: string
  note: string | null
  /** Whose `paid` line it is — the person being asked about, not the writer. */
  fromUserId: UserId
  fromDisplayName: string | null
  recordedBy: UserId
  recordedAt: string
  /** The project the payment was tagged to — what it was for — or `null`. */
  projectId: string | null
  projectName: string | null
}

export type AwaitingSettlementsResponse = {
  status: 'ok'
  settlements: AwaitingSettlement[]
  nextCursor: string | null
}

export type Allocation = { userId: UserId; amountCents: number }

export type Transaction = {
  id: string
  groupId: string
  kind: 'expense' | 'loan' | 'repayment'
  description: string
  /** One of `src/lib/categories.ts`, or `null` for uncategorised. */
  category: string | null
  amountCents: number
  currency: string
  occurredOn: string
  note: string | null
  splitMode: string
  /**
   * What was typed to arrive at the lines, in the mode's own units — which is
   * what re-opens this expense in the sheet as it was entered rather than as
   * cents somebody has to guess percentages back out of.
   *
   * `null` for a row written before migration 0010. The sheet reads that as
   * "take `owed` as exact amounts", which is the one reading of a set of
   * cents that cannot be wrong; see `src/lib/split-draft.ts`.
   */
  split: Split | null
  createdBy: UserId
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  /**
   * When the person a settlement was paid to said it arrived, and who that
   * was. Always `null` on an expense — there is nobody for an expense to be
   * waiting on — and `null` on a settlement nobody has acknowledged yet.
   *
   * It moves no balance either way. The lines counted the moment they were
   * written, because the money moved when it moved; this is the reassurance
   * beside them. See `server/settlements.ts`.
   */
  confirmedAt: string | null
  confirmedBy: UserId | null
  /**
   * The one project of the group's it is filed in, or `null`. A label and not
   * money: moving it between projects changes nobody's balance.
   */
  projectId: string | null
  paid: Allocation[]
  owed: Allocation[]
  receiptCount: number
  /**
   * Set on a row this browser has filed but not yet had answered. It is not a
   * column; nothing on the server ever sends it. See `src/lib/optimistic.ts`.
   */
  pending?: true
  /**
   * Set on a `pending` row this browser could not even send — there was no
   * network to send it on, and `src/queue.ts` is holding it for when there
   * is. Never both `pending` and `queued` being false while the other is
   * true is not a rule the type enforces, only a fact of how the two are set:
   * a row is queued instead of merely pending, never in addition to being
   * something else.
   */
  queued?: true
}

export type TransactionsResponse = {
  status: 'ok'
  transactions: Transaction[]
  nextCursor: string | null
}

export type ActivityEntry = {
  id: string
  groupId: string
  groupName: string
  actorId: UserId | null
  actorDisplayName: string | null
  kind: string
  subjectType: string | null
  subjectId: string | null
  data: Record<string, unknown>
  createdAt: string
}

export type ActivityResponse = {
  status: 'ok'
  activity: ActivityEntry[]
  nextCursor: string | null
}

/** One group's own feed — the same rows as `ActivityResponse`, plus which group they are. */
export type GroupActivityResponse = ActivityResponse & {
  group: { id: string; name: string; currency: string }
}

/**
 * One attached file, as the API describes it.
 *
 * The two URLs come from the server rather than being composed here, and both
 * are paths through the Worker. That is not a convenience: it is the one place
 * a client could otherwise learn to build a bucket URL, and there is no bucket
 * URL to build — the bucket is private and every read is re-authorised. See
 * `server/receipts.ts`.
 */
export type Receipt = {
  id: string
  groupId: string
  uploadedBy: UserId
  contentType: string
  sizeBytes: number
  thumbBytes: number
  /** What this receipt costs against the uploader's storage limit. */
  storedBytes: number
  originalName: string | null
  createdAt: string
  url: string
  /** The same file as `attachment` — what the viewer's Download points at. */
  downloadUrl: string
  /** `null` when there is no thumbnail; a screen shows the file's icon. */
  thumbnailUrl: string | null
  /**
   * `null` when this was photographed before there was an expense to file it
   * on, which is the ordinary case rather than a broken one: the receipt is
   * taken at the till and the expense is filed that evening.
   */
  transactionId: string | null
  /**
   * `'pending' | 'done' | 'failed' | 'off'` — where the receipt OCR read is,
   * from `server/receipt-ocr.ts`. `off` covers both a free account (the model
   * is never called) and a PDF (there is no photograph to read); nothing on
   * this screen has to tell those apart, since neither ever has an amount to
   * show.
   */
  ocrStatus: 'pending' | 'done' | 'failed' | 'off'
  /**
   * What the model read, in cents — a suggestion, never a decision. `null`
   * until the read finishes, and `null` forever on a `failed` one: a failed
   * read leaves the amount field empty rather than wrong.
   */
  ocrAmountCents: number | null
}

export type ReceiptsResponse = { status: 'ok'; receipts: Receipt[] }

/** The expense a receipt is filed on, as much of it as a tile needs. */
export type LinkedTransaction = {
  id: string
  description: string
  category: string | null
  kind: string
  amountCents: number
  currency: string
  occurredOn: string
  /**
   * Set when the expense was removed *after* this receipt was filed on it.
   * The server does not rewrite the link — what happened, happened — so the
   * screen is told and treats the receipt as loose again.
   */
  deletedAt: string | null
}

/** A receipt as the library sends it: the row, plus what it is filed on. */
export type LibraryReceipt = Receipt & { transaction: LinkedTransaction | null }

export type ReceiptLibraryResponse = {
  status: 'ok'
  receipts: LibraryReceipt[]
  /** How many in the whole group are waiting; with the first page only. */
  unfiled: number | null
  nextCursor: string | null
}

export type FiledReceiptResponse = {
  status: 'ok'
  receipt: Receipt
  transaction: LinkedTransaction | null
}

export type UploadedReceiptResponse = {
  status: 'ok'
  receipt: Receipt
  /** Set when this file was already on the expense; nothing was stored. */
  duplicate?: true
  storage?: Storage
}

/** What one limit is, as `GET /api/me` reports it. */
export type Limit = {
  value: number | null
  unlimited: boolean
  source: 'override' | 'plan' | 'fallback'
  expiresAt: string | null
}

/**
 * Storage as `/api/me` reports it: what is used, what is allowed, and the
 * largest single file. It is what the quota bar is drawn from *before* an
 * upload, and it is a courtesy and never the enforcement — the server takes
 * every one of these numbers again, from the limit resolver, on the request
 * that stores the bytes.
 */
export type Storage = {
  usedBytes: number
  limitBytes: number | null
  unlimited: boolean
  maxFileBytes: number
}

export type MeResponse = {
  status: 'ok'
  user: { id: UserId; displayName: string; email: string }
  planId: string | null
  limits: Record<string, Limit>
  storage: Storage
  theme: ResolvedTheme
  motion: MotionPreference
}

/**
 * One row of `themes`, and whether this account may have it.
 *
 * `premium` is the column; `available` is that column resolved against the
 * caller's `premium_themes` limit on the request that answered. Both are sent
 * because the picker says two different things with them — "Premium" beside
 * the name, and the row greyed out — and only the first is true for everybody.
 */
export type ThemeSummary = {
  id: string
  name: string
  premium: boolean
  isDefault: boolean
  available: boolean
}

/** Which theme is on the screen, which was chosen, and whether those differ. */
export type ResolvedTheme = {
  /** What goes in `data-theme`. Always something `src/theme.css` has a block for. */
  id: string
  /** What is on the user row. `null` is "the default". */
  chosenId: string | null
  /** Their choice is a premium theme they may not use today. */
  locked: boolean
}

export type ThemesResponse = ResolvedTheme & {
  status: 'ok'
  themes: ThemeSummary[]
  premiumAllowed: boolean
}

export type CreatedTransactionResponse = {
  status: 'ok'
  transaction: Transaction
  /** The group's whole ledger after the write — what the balances reconcile to. */
  ledger: {
    edges: { debtorId: UserId; creditorId: UserId; amountCents: number }[]
    balances: { userId: UserId; amountCents: number }[]
  }
}

// ---------------------------------------------------------------------------
// The calls
// ---------------------------------------------------------------------------

/**
 * The home screen asks for its people in one page rather than paging them.
 * `/api/balances` pages by *person*, and the number of people you have money
 * between you with is a much smaller number than the number of expenses you
 * have — so one page is the whole answer for anybody real, and the totals
 * (which come with the first page only) then describe exactly the list on the
 * screen. `MAX_LIMIT` on the server is 200; this asks for it.
 */
export const HOME_PEOPLE_LIMIT = 200

export const fetchBalances = (fetchImpl?: typeof fetch) =>
  request<BalancesResponse>(`/api/balances?limit=${HOME_PEOPLE_LIMIT}`, {}, fetchImpl)

export const fetchGroups = (fetchImpl?: typeof fetch) =>
  request<GroupsResponse>('/api/groups', {}, fetchImpl)

/**
 * Makes one, with you as its owner and only member.
 *
 * The `Idempotency-Key` is not a formality here. A request that got through
 * and then lost its answer would otherwise leave a second group with the same
 * name, which is the kind of duplicate somebody has to tell apart by hand.
 * The server replays the first answer for a repeated key
 * (`server/idempotency.ts`), so a flaky connection costs a second request
 * rather than a second group.
 *
 * `currency` is optional because the server defaults it to USD, but the form
 * always sends one: `PATCH` refuses a currency change once the group has any
 * transactions, so the moment to get it right is this one.
 */
export function createGroup(
  draft: { name: string; currency?: string },
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<CreatedGroupResponse> {
  return request<CreatedGroupResponse>(
    '/api/groups',
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(draft),
    },
    fetchImpl,
  )
}

export const fetchGroup = (groupId: string, fetchImpl?: typeof fetch) =>
  request<GroupResponse>(`/api/groups/${encodeURIComponent(groupId)}`, {}, fetchImpl)

export const fetchGroupBalances = (groupId: string, fetchImpl?: typeof fetch) =>
  request<GroupBalancesResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/balances`,
    {},
    fetchImpl,
  )

/**
 * Who should pay whom to clear this group.
 *
 * `simplify` left out asks for whatever the group itself decided, which is
 * what an ordinary read wants. The settle-up screen passes it explicitly, and
 * that is not belt and braces: flipping the switch moves the screen before the
 * `PATCH` has landed, so a plan fetched without the parameter in that moment
 * would come back answering the *old* setting and look like the switch had
 * failed. Naming the question makes the answer independent of which request
 * won.
 */
export const fetchSettlementPlan = (
  groupId: string,
  simplify?: boolean,
  fetchImpl?: typeof fetch,
) =>
  request<SettlementPlanResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/settlement-plan` +
      (simplify === undefined ? '' : `?simplify=${simplify}`),
    {},
    fetchImpl,
  )

/** The payments somebody says they made to you, that you have not confirmed. */
export const fetchAwaitingSettlements = (groupId: string, fetchImpl?: typeof fetch) =>
  request<AwaitingSettlementsResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/settlements/awaiting`,
    {},
    fetchImpl,
  )

/**
 * "It landed." Sets `confirmedAt` on a settlement, and moves nothing else: the
 * balance changed when the payment was recorded, because that is when the
 * money moved. Only the person paid may say so, and saying it twice is not an
 * error.
 */
export function confirmSettlement(
  groupId: string,
  transactionId: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<{
  status: 'ok'
  transactionId: string
  confirmedAt: string
  confirmedBy: UserId
  alreadyConfirmed: boolean
}> {
  return request(
    `/api/groups/${encodeURIComponent(groupId)}/settlements/` +
      `${encodeURIComponent(transactionId)}/confirm`,
    { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

/**
 * "It did not." The nudge's other answer, from the same person: the money
 * never turned up, so the payment comes off.
 *
 * It answers with the group's whole ledger after the removal — the same body
 * a create, an edit and a delete answer with — because a rejection puts a
 * balance back and the screen that asked has to show it. That is why it is
 * typed as a `CreatedTransactionResponse` rather than as something of its own:
 * `server/settlements.ts` performs the identical soft delete
 * `deleteTransaction` does, and the reply is the same reply.
 *
 * Only the person paid may send it, and sending it twice is not an error.
 */
export function rejectSettlement(
  groupId: string,
  transactionId: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<CreatedTransactionResponse> {
  return request<CreatedTransactionResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/settlements/` +
      `${encodeURIComponent(transactionId)}/reject`,
    { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

/**
 * Changes something about the group itself: its name, its currency, whether
 * it is archived, or `simplifyDebts`. `server/groups.ts` is the authority on
 * who may send which field — the first three are the owner's, the last is any
 * member's.
 */
export function updateGroup(
  groupId: string,
  patch: { name?: string; currency?: string; archived?: boolean; simplifyDebts?: boolean },
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<{ status: 'ok'; group: GroupSummary; changed: string[] }> {
  return request(
    `/api/groups/${encodeURIComponent(groupId)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(patch),
    },
    fetchImpl,
  )
}

/**
 * A group's transactions, newest first. `projectId` narrows them to one
 * project's, or — as `'none'` — to what is in no project at all; left out, it
 * is the whole group, which is what the group screen lists.
 */
export const fetchTransactions = (
  groupId: string,
  fetchImpl?: typeof fetch,
  projectId?: string,
) =>
  request<TransactionsResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/transactions` +
      (projectId === undefined ? '' : `?projectId=${encodeURIComponent(projectId)}`),
    {},
    fetchImpl,
  )

export const fetchActivity = (limit = 12, fetchImpl?: typeof fetch) =>
  request<ActivityResponse>(`/api/activity?limit=${limit}`, {}, fetchImpl)

/** One group's own feed, a page at a time — `server/activity.ts`'s `groupActivity` router. */
export function fetchGroupActivity(
  groupId: string,
  options: { cursor?: string | null } = {},
  fetchImpl?: typeof fetch,
): Promise<GroupActivityResponse> {
  const query = new URLSearchParams()
  if (options.cursor) query.set('cursor', options.cursor)
  const suffix = query.toString()
  return request<GroupActivityResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/activity${suffix ? `?${suffix}` : ''}`,
    {},
    fetchImpl,
  )
}

/**
 * Where the group's CSV export lives. Composed rather than returned by the
 * server, unlike a receipt's URL: a group id is not a secret the way a
 * receipt id is (`server/receipts.ts` says why that one has to come from the
 * server), so there is nothing here a client could learn that it did not
 * already have. It is a plain path, not a `fetch` — `<a download>` is what
 * turns `Content-Disposition: attachment` into a saved file, and a `fetch`
 * into a blob URL would hold a copy of it in memory for no reason.
 */
export const groupExportUrl = (groupId: string) =>
  `/api/groups/${encodeURIComponent(groupId)}/export.csv`

/**
 * Who you are, what you are allowed and how much of the storage you have
 * spent — the one request the app makes to be able to grey a button out
 * instead of offering something that will be refused.
 */
export const fetchMe = (fetchImpl?: typeof fetch) =>
  request<MeResponse>('/api/me', {}, fetchImpl)

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

/**
 * The catalogue, and where this account currently lands in it. The SPA asks
 * for this once on open, beside the session: the answer is both what the
 * picker lists and what `data-theme` should say, and asking two questions to
 * get them would be two chances for the list and the choice to disagree.
 */
export const fetchThemes = (fetchImpl?: typeof fetch) =>
  request<ThemesResponse>('/api/themes', {}, fetchImpl)

/**
 * Pick one. `null` is "the default", not "no theme".
 *
 * No `Idempotency-Key`: this `PUT` is the whole value of the column, so a
 * retry sets it to the same thing. `server/themes.ts` says the same from the
 * other end.
 */
export const chooseTheme = (themeId: string | null, fetchImpl?: typeof fetch) =>
  request<ThemesResponse>(
    '/api/themes/chosen',
    {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ themeId }),
    },
    fetchImpl,
  )

// ---------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------

/**
 * How much this account wants the app to move: `system`, `full` or `reduced`.
 *
 * There is no fetcher beside it. What the preference is arrives on the session
 * and on `/api/me`, both of which every screen already has — and the first
 * list of rows has staggered in long before a request of its own could come
 * back, which is exactly the animation this preference exists to be able to
 * stop. `src/motion.ts` says the same thing about `localStorage`.
 *
 * No `Idempotency-Key`, for the same reason `chooseTheme` has none: the body
 * is the whole value of the column.
 */
export const chooseMotion = (preference: MotionPreference, fetchImpl?: typeof fetch) =>
  request<{ status: 'ok'; preference: MotionPreference }>(
    '/api/motion',
    {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ preference }),
    },
    fetchImpl,
  )

// ---------------------------------------------------------------------------
// Invites, and the placeholder one of them claims
// ---------------------------------------------------------------------------

/** What a link is for: handing one named person their row, or letting anyone in. */
export type InviteKind = 'claim' | 'join'

/** Only `live` means "follow me"; the other three are why it will not work. */
export type InviteState = 'live' | 'revoked' | 'expired' | 'used'

export type Invite = {
  id: string
  kind: InviteKind
  state: InviteState
  /** Non-null pins the link to one address; null is anyone holding it. */
  email: string | null
  createdBy: UserId
  createdAt: string
  expiresAt: string
  revokedAt: string | null
  uses: number
  /** null means unlimited, exactly as a limit's value does. */
  maxUses: number | null
  placeholderUserId: UserId | null
  placeholderDisplayName: string | null
}

export type CreatedInviteResponse = {
  status: 'ok'
  group: GroupRef
  invite: Invite
  /**
   * The one time this exists outside the message it is going into. Only its
   * SHA-256 is stored, so nothing — this API included — can produce it again,
   * which is why the screen has to put it in front of somebody now.
   */
  token: string
  url: string
}

export type InvitesResponse = {
  status: 'ok'
  group: GroupRef
  invites: Invite[]
  nextCursor: string | null
}

/**
 * What a link is for, read *before* following it — and read by somebody who is
 * very often not signed in yet, which is why this is the one call in this file
 * that expects to be made by a stranger.
 */
export type InviteLandingResponse = {
  status: 'ok'
  invite: Omit<Invite, 'placeholderDisplayName' | 'createdBy'> & {
    group: { id: string; name: string; currency: string; archivedAt: string | null }
    /** Whoever minted it. A link with a name on it is a link people follow. */
    invitedByDisplayName: string | null
    placeholder: { userId: UserId; displayName: string; claimed: boolean } | null
  }
  you: {
    signedIn: boolean
    userId: UserId | null
    displayName: string | null
    alreadyInGroup: boolean
    /**
     * Whether following this link would pour an existing account into somebody
     * else's row. It is what the warning on the screen is drawn from, and the
     * server refuses without `acknowledgeMerge` when it is true — the client's
     * copy is the courtesy, never the enforcement.
     */
    mergesAccount: boolean
    groupCount: number
  }
}

export type AcceptedInviteResponse = {
  status: 'ok'
  group: GroupRef
  claimed: boolean
  added?: boolean
  rejoined?: boolean
  member: Member | null
  /** Present on a claim: who you are now. Your user id has changed. */
  user?: { id: UserId; displayName: string; email: string }
  previousUserId?: UserId
}

const invitesPath = (groupId: string) => `/api/groups/${encodeURIComponent(groupId)}/invites`

export const fetchInvites = (groupId: string, fetchImpl?: typeof fetch) =>
  request<InvitesResponse>(invitesPath(groupId), {}, fetchImpl)

/**
 * Mints one. The token comes back exactly once, in this response, because only
 * its hash is stored — so a screen that does not show it here can never show
 * it at all.
 */
export function createInvite(
  groupId: string,
  options: { placeholderUserId?: UserId; email?: string | null; maxUses?: number | null },
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<CreatedInviteResponse> {
  return request(
    invitesPath(groupId),
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(options),
    },
    fetchImpl,
  )
}

export function revokeInvite(
  groupId: string,
  inviteId: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<{ status: 'ok'; invite: Invite; revoked: boolean }> {
  return request(
    `${invitesPath(groupId)}/${encodeURIComponent(inviteId)}`,
    { method: 'DELETE', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

/** Reads a link without spending it. No session needed. */
export const fetchInviteLanding = (token: string, fetchImpl?: typeof fetch) =>
  request<InviteLandingResponse>(`/api/invites/${encodeURIComponent(token)}`, {}, fetchImpl)

/**
 * Follows it. `acknowledgeMerge` is the same shape as `?acknowledgeBalance` on
 * leaving a group with money outstanding: the server refuses first and names
 * what is about to happen, and this is the second request that says yes.
 */
export function acceptInvite(
  token: string,
  options: { acknowledgeMerge?: boolean },
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<AcceptedInviteResponse> {
  return request(
    `/api/invites/${encodeURIComponent(token)}/accept` +
      (options.acknowledgeMerge ? '?acknowledgeMerge=true' : ''),
    { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

/**
 * Adds somebody to a group with nothing but a name — a placeholder, which is a
 * real member of the ledger from the next expense onwards and not a note to
 * self. `server/members.ts` is the other end of it.
 */
export function addPlaceholderMember(
  groupId: string,
  displayName: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<{ status: 'ok'; group: GroupRef; member: Member; added: boolean }> {
  return request(
    `/api/groups/${encodeURIComponent(groupId)}/members`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ displayName }),
    },
    fetchImpl,
  )
}

const receiptsPath = (groupId: string, transactionId: string) =>
  `/api/groups/${encodeURIComponent(groupId)}/transactions/` +
  `${encodeURIComponent(transactionId)}/receipts`

export const fetchReceipts = (groupId: string, transactionId: string, fetchImpl?: typeof fetch) =>
  request<ReceiptsResponse>(receiptsPath(groupId, transactionId), {}, fetchImpl)

/**
 * Puts one up: the re-encoded file and its thumbnail, in a single multipart
 * POST.
 *
 * There is no `Idempotency-Key` here, and that is deliberate rather than an
 * omission. A key works by hashing the request body, which for several
 * megabytes of image is expensive twice over — and the file already carries
 * something better: the server stores its SHA-256, so a retry of an upload
 * whose reply was lost is answered with the receipt that is already there.
 * `server/receipts.ts` makes the whole argument.
 *
 * The `content-type` header is deliberately *not* set: `fetch` composes the
 * multipart boundary itself, and naming the type by hand is the classic way to
 * send a body the server cannot parse.
 */
export function uploadReceipt(
  groupId: string,
  transactionId: string,
  upload: { file: Blob; thumbnail: Blob | null; originalName: string },
  fetchImpl?: typeof fetch,
): Promise<UploadedReceiptResponse> {
  const form = new FormData()
  form.set('file', upload.file, upload.originalName)
  if (upload.thumbnail) form.set('thumb', upload.thumbnail, 'thumbnail.webp')
  form.set('originalName', upload.originalName)

  return request<UploadedReceiptResponse>(
    receiptsPath(groupId, transactionId),
    { method: 'POST', body: form },
    fetchImpl,
  )
}

/**
 * Takes one off. Soft on the server — the object waits a week for the weekly
 * sweep — so this is recoverable by a human for as long as that lasts, and
 * doing it twice is not an error.
 */
export function deleteReceipt(
  groupId: string,
  transactionId: string,
  receiptId: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<{ status: 'ok'; receiptId: string; alreadyGone: boolean }> {
  return request(
    `${receiptsPath(groupId, transactionId)}/${encodeURIComponent(receiptId)}`,
    { method: 'DELETE', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

// ---------------------------------------------------------------------------
// The receipt library
// ---------------------------------------------------------------------------

/** Which pile the grid is asking for, as the server's query parameter. */
export type ReceiptFilter = 'all' | 'unfiled' | 'filed'

const libraryPath = (groupId: string) =>
  `/api/groups/${encodeURIComponent(groupId)}/receipts`

/**
 * A group's whole pile, unfiled first, paged by keyset.
 *
 * The grid draws every tile from `thumbnailUrl` and never from `url`; see
 * `src/lib/receipt-library.ts`, which is where that is a function rather than
 * a habit. This call is only the transport.
 */
export function fetchReceiptLibrary(
  groupId: string,
  options: { filter?: ReceiptFilter; uploader?: string | null; cursor?: string | null } = {},
  fetchImpl?: typeof fetch,
): Promise<ReceiptLibraryResponse> {
  const query = new URLSearchParams()
  if (options.filter && options.filter !== 'all') query.set('filter', options.filter)
  if (options.uploader) query.set('uploader', options.uploader)
  if (options.cursor) query.set('cursor', options.cursor)

  const suffix = query.toString()
  return request<ReceiptLibraryResponse>(
    `${libraryPath(groupId)}${suffix ? `?${suffix}` : ''}`,
    {},
    fetchImpl,
  )
}

/**
 * One receipt on its own, with whatever it is filed on.
 *
 * The viewer is a URL, so it has to open from a cold start — a link somebody
 * was sent, a refresh, or a thumbnail pressed under an expense whose receipt
 * is four pages down the grid. Reading it out of the list would be a viewer
 * that works for recent receipts and silently does nothing for old ones.
 */
export const fetchReceipt = (groupId: string, receiptId: string, fetchImpl?: typeof fetch) =>
  request<{ status: 'ok'; receipt: LibraryReceipt }>(
    `${libraryPath(groupId)}/${encodeURIComponent(receiptId)}`,
    {},
    fetchImpl,
  )

/**
 * Puts one up with no expense on it — the photograph taken at the till.
 *
 * The same multipart body as an upload onto an expense, at a path with no
 * transaction in it, and answered by the same code on the other end. There is
 * no `Idempotency-Key` here for the same reason there is none there: the
 * file's SHA-256 is stored, so a retry whose reply was lost is answered with
 * the receipt that is already in the pile.
 */
export function uploadLooseReceipt(
  groupId: string,
  upload: { file: Blob; thumbnail: Blob | null; originalName: string },
  fetchImpl?: typeof fetch,
): Promise<UploadedReceiptResponse> {
  const form = new FormData()
  form.set('file', upload.file, upload.originalName)
  if (upload.thumbnail) form.set('thumb', upload.thumbnail, 'thumbnail.webp')
  form.set('originalName', upload.originalName)

  return request<UploadedReceiptResponse>(
    libraryPath(groupId),
    { method: 'POST', body: form },
    fetchImpl,
  )
}

/**
 * Files a receipt on an expense — or takes it off, with `null`.
 *
 * This is the other end of the flow the loose upload exists for, and it is
 * the same call whether the expense was already there or has just been made
 * from the receipt itself.
 */
export function fileReceipt(
  groupId: string,
  receiptId: string,
  transactionId: string | null,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<FiledReceiptResponse> {
  return request<FiledReceiptResponse>(
    `${libraryPath(groupId)}/${encodeURIComponent(receiptId)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ transactionId }),
    },
    fetchImpl,
  )
}

/** Takes one out of the library, filed or not. Soft, like every delete here. */
export function deleteLibraryReceipt(
  groupId: string,
  receiptId: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<{ status: 'ok'; receiptId: string; alreadyGone: boolean }> {
  return request(
    `${libraryPath(groupId)}/${encodeURIComponent(receiptId)}`,
    { method: 'DELETE', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

/**
 * What the expense sheet collects. The server resolves it to cents.
 *
 * `payers` and `split.participants` are both in the group's member order,
 * because that is the order an odd cent is broken by — the client predicts
 * the split with the same `resolveSplit` the server will run, and predicting
 * it in a different order predicts the wrong person's cent. The server sorts
 * them again anyway; it does not take the order on trust from anybody.
 */
export type ExpenseDraft = {
  description: string
  category: string | null
  amountCents: number
  occurredOn: string
  /** One entry covering the whole amount is the common case, not a special one. */
  payers: Allocation[]
  split: Split
  kind?: 'expense' | 'loan' | 'repayment'
  /**
   * Which project to file it in. Left out is "leave it where it is" — on a
   * new expense that is no project, and on a correction it is whatever the
   * row was already in, which matters: re-sending an archived project's id
   * is refused (`project_archived`) even when it is the one the row is in.
   * `null` takes it out of its project. `src/lib/projects.ts`'s
   * `projectIdToSend` decides which of the three a sheet sends.
   */
  projectId?: string | null
}

/**
 * Who put the money in, as the API reads it.
 *
 * A single payer covering the whole amount is sent as `paidBy` rather than a
 * one-element `payers`, which is the shorthand the API documents for the
 * common case and keeps the request that a phone sends identical to the one a
 * laptop sends for the same expense.
 *
 * Shared with `recurringBody` below, and not by coincidence: a recurring rule
 * is stored in exactly the shape `POST .../transactions` is read into
 * (`parseMoney`, the same function either way — see `server/recurring.ts`), so
 * the two requests describe the same money the same way or one of them is
 * wrong.
 */
function payersBody(payers: Allocation[], amountCents: number) {
  const solePayer =
    payers.length === 1 && payers[0].amountCents === amountCents ? payers[0].userId : null

  return solePayer === null ? { payers } : { paidBy: solePayer }
}

/**
 * A draft as the API reads it.
 *
 * The split goes as a *mode and its inputs*, never as the cents each person
 * owes: the server resolves it through the same `src/lib/money.ts` this app
 * previews with, so there is one implementation of largest-remainder rounding
 * in the system rather than two that can disagree about a penny.
 */
function expenseBody(draft: ExpenseDraft) {
  return {
    kind: draft.kind ?? 'expense',
    description: draft.description,
    category: draft.category,
    amountCents: draft.amountCents,
    occurredOn: draft.occurredOn,
    ...payersBody(draft.payers, draft.amountCents),
    split: draft.split,
    ...(draft.projectId === undefined ? {} : { projectId: draft.projectId }),
  }
}

/**
 * The path and body of a `createTransaction` request, without sending it.
 *
 * This is what `src/queue-document.ts` needs to queue a write it cannot
 * reach the network for: the exact bytes to send later are the exact bytes
 * that would have gone now, computed once so there is one place that turns a
 * draft into a request body.
 */
export function transactionRequest(
  groupId: string,
  draft: ExpenseDraft,
): { path: string; body: string } {
  return {
    path: `/api/groups/${encodeURIComponent(groupId)}/transactions`,
    body: JSON.stringify(expenseBody(draft)),
  }
}

/**
 * Files an expense.
 *
 * The `Idempotency-Key` is the client's, minted once per draft and reused on
 * every retry of *that* draft — which is the whole point of it: an optimistic
 * screen that has already moved the balance must be able to retry a request
 * whose reply was lost without filing the expense twice. See
 * `server/idempotency.ts`.
 */
export function createTransaction(
  groupId: string,
  draft: ExpenseDraft,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<CreatedTransactionResponse> {
  const { path, body } = transactionRequest(groupId, draft)
  return request<CreatedTransactionResponse>(
    path,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body,
    },
    fetchImpl,
  )
}

/**
 * Corrects one — the same draft, at a `PATCH`.
 *
 * It sends the whole of the money every time, because the API has no partial
 * money edit and should not: a new amount with the old percentages is a
 * different set of cents, and an edit that sent half of it would be asking the
 * server to guess which half of the old inputs still applied. That is also why
 * the sheet re-opens from `transaction.split` rather than from the stored
 * cents — an editor that has to guess the inputs would change somebody's
 * expense as the price of opening it.
 */
export function updateTransaction(
  groupId: string,
  transactionId: string,
  draft: ExpenseDraft,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<CreatedTransactionResponse> {
  return request<CreatedTransactionResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/transactions/${encodeURIComponent(transactionId)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(expenseBody(draft)),
    },
    fetchImpl,
  )
}

/**
 * Takes one back — the soft delete, at a `DELETE`.
 *
 * There is no separate "cancel a settlement" call for the same reason there
 * is no separate "record a settlement" one: a settlement is an ordinary
 * `transactions` row with `kind: "repayment"` (`src/lib/settle.ts`), so
 * undoing it is the delete that already exists rather than a second write
 * path into the ledger. `src/queries.ts`'s `useCancelSettlement` is the
 * intention; this is the transport, and it is honest about which route it is.
 *
 * The reply carries the group's whole ledger after the removal, exactly as a
 * create and an edit do, so the balances are put back from the response
 * rather than by a second request. Deleting something already deleted is not
 * an error — it answers with the state it found.
 */
export function deleteTransaction(
  groupId: string,
  transactionId: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<CreatedTransactionResponse> {
  return request<CreatedTransactionResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/transactions/${encodeURIComponent(transactionId)}`,
    { method: 'DELETE', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

/** 128 bits, which is enough that two drafts never collide. */
export function newIdempotencyKey(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

/**
 * A named bucket inside a group — "Cabin Weekend – food". A label on some of
 * the group's transactions and never a ledger of its own: `server/projects.ts`
 * makes the argument. `settled` is whether everybody's net across its
 * transactions is zero; an empty project is settled.
 *
 * An archived project (`archivedAt` set) is locked: nothing goes in or comes
 * out of it, and it is not renamed, until it is unarchived.
 */
export type Project = {
  id: string
  groupId: string
  name: string
  createdBy: UserId
  createdAt: string
  updatedAt: string
  archivedAt: string | null
  transactionCount: number
  settled: boolean
}

/** Open ones first, then archived, newest first within each — the server's order. */
export type ProjectsResponse = { status: 'ok'; projects: Project[] }

export type ProjectResponse = { status: 'ok'; project: Project }

/** A bulk attach or detach: the project after it, and the ids it actually moved. */
export type ProjectTransactionsResponse = ProjectResponse & { transactionIds: string[] }

const projectsPath = (groupId: string) => `/api/groups/${encodeURIComponent(groupId)}/projects`
const projectPath = (groupId: string, projectId: string) =>
  `${projectsPath(groupId)}/${encodeURIComponent(projectId)}`

/** Every project in the group, archived ones included. Not paged: a group has a handful. */
export const fetchProjects = (groupId: string, fetchImpl?: typeof fetch) =>
  request<ProjectsResponse>(projectsPath(groupId), {}, fetchImpl)

export const fetchProject = (groupId: string, projectId: string, fetchImpl?: typeof fetch) =>
  request<ProjectResponse>(projectPath(groupId, projectId), {}, fetchImpl)

/**
 * Where everybody stands *within* one project: what they paid less what they
 * owed across its transactions. The group's `MemberBalance` rows, the same
 * sign — positive, the project owes them — and `settled` over the whole
 * project rather than this page.
 */
export type ProjectBalancesResponse = GroupBalancesResponse & {
  project: Project
  settled: boolean
}

/** The group's plan, in the same shape, over one project's lines only. */
export type ProjectSettlementPlanResponse = SettlementPlanResponse & { project: Project }

export const fetchProjectBalances = (
  groupId: string,
  projectId: string,
  fetchImpl?: typeof fetch,
) => request<ProjectBalancesResponse>(`${projectPath(groupId, projectId)}/balances`, {}, fetchImpl)

/**
 * Who pays whom to clear one project — `GET .../projects/:id/settlement-plan`,
 * not the group's `/settlement-plan`. The group's plan nets everything the
 * group has ever spent, so a suggestion read off it is not what *this*
 * project owes, and a payment tagged to the project for that amount would
 * leave the project unsettled. `simplify` works as `fetchSettlementPlan`'s
 * does, for the same reason.
 */
export const fetchProjectSettlementPlan = (
  groupId: string,
  projectId: string,
  simplify?: boolean,
  fetchImpl?: typeof fetch,
) =>
  request<ProjectSettlementPlanResponse>(
    `${projectPath(groupId, projectId)}/settlement-plan` +
      (simplify === undefined ? '' : `?simplify=${simplify}`),
    {},
    fetchImpl,
  )

/**
 * Makes one, and — in the same request — moves `transactionIds` into it, so a
 * project is never briefly empty on its way to holding what it was made for.
 * A transaction already in another open project is moved, not copied.
 */
export function createProject(
  groupId: string,
  project: { name: string; transactionIds?: readonly string[] },
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<ProjectResponse> {
  const transactionIds = project.transactionIds ?? []
  return request<ProjectResponse>(
    projectsPath(groupId),
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({
        name: project.name,
        ...(transactionIds.length > 0 ? { transactionIds } : {}),
      }),
    },
    fetchImpl,
  )
}

export function renameProject(
  groupId: string,
  projectId: string,
  name: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<ProjectResponse> {
  return request<ProjectResponse>(
    projectPath(groupId, projectId),
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ name }),
    },
    fetchImpl,
  )
}

/** Archive (`true`) or unarchive (`false`). Asking for the state it is already in is not an error. */
export function setProjectArchived(
  groupId: string,
  projectId: string,
  archived: boolean,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<ProjectResponse> {
  return request<ProjectResponse>(
    `${projectPath(groupId, projectId)}/${archived ? 'archive' : 'unarchive'}`,
    { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

/**
 * Puts these transactions in the project (`attach`), or takes them out of it.
 * Detaching leaves anything that was not in *this* project where it is.
 */
export function moveProjectTransactions(
  groupId: string,
  projectId: string,
  transactionIds: readonly string[],
  attach: boolean,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<ProjectTransactionsResponse> {
  return request<ProjectTransactionsResponse>(
    `${projectPath(groupId, projectId)}/transactions`,
    {
      method: attach ? 'POST' : 'DELETE',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ transactionIds }),
    },
    fetchImpl,
  )
}

// ---------------------------------------------------------------------------
// Recurring expenses
// ---------------------------------------------------------------------------

/**
 * A recurring rule: a template expense and the date it next falls due.
 *
 * It is *inputs*, not lines — `split` and `paid` are exactly what the sheet
 * that made it typed, and every occurrence resolves them fresh through the
 * same `src/lib/money.ts` an ordinary expense goes through, in the group's
 * member order as it stands on the day it fires. So there is no `owed` here
 * to read and no total per person to show: what one occurrence costs whom is
 * a question about a month, and only the transaction it made can answer it.
 *
 * Nothing on this client ever materialises one. That is the scheduled
 * Worker's job (`server/recurring.ts`'s `materializeDueRecurring`, run from
 * `worker/src/index.ts`), which is why a rule due yesterday can still be
 * sitting here saying so: the app is looking at a cursor a cron moves.
 */
export type RecurringRule = {
  id: string
  groupId: string
  /** Never `repayment`: a settlement happens on the day it happens. */
  kind: 'expense' | 'loan'
  description: string
  category: string | null
  amountCents: number
  splitMode: string
  split: Split
  paid: Allocation[]
  note: string | null
  /** `YYYY-MM-DD`, on the 1st–28th. The first occurrence, and the day of every one after it. */
  startOn: string
  /** The next date due — the Worker's cursor, and the only date that moves. */
  nextOccursOn: string
  /** Inclusive, and optional: `null` runs until somebody cancels it. */
  endOn: string | null
  createdBy: UserId
  createdAt: string
  updatedAt: string
  /** Set when the series was cancelled. The list only ever carries live rules. */
  deletedAt: string | null
  /** The project every occurrence is filed in, or `null`. */
  projectId: string | null
}

/** One calendar date, having been handled — by making a transaction, or by being skipped. */
export type RecurringOccurrence = {
  occursOn: string
  status: 'materialized' | 'skipped'
  /** The expense it made, or `null` on a skip. */
  transactionId: string | null
  createdAt: string
}

export type RecurringResponse = {
  status: 'ok'
  recurring: RecurringRule[]
  nextCursor: string | null
}

export type RecurringRuleResponse = {
  status: 'ok'
  recurring: RecurringRule
  /** The last dozen, newest first. */
  occurrences: RecurringOccurrence[]
}

export type SavedRecurringResponse = { status: 'ok'; recurring: RecurringRule }

/** `skipped` is the date that was skipped — the rule's own `nextOccursOn`. */
export type SkippedRecurringResponse = SavedRecurringResponse & { skipped: string }

/**
 * What the recurring sheet collects — the expense draft's fields, plus the
 * two that make it a schedule rather than an expense.
 *
 * `startOn` is create-only. `PATCH` does not read it, and this file does not
 * send it: changing which date a series falls on is changing what the series
 * *is*, and `server/recurring.ts` says why the honest way to do that is to
 * cancel this one and start another.
 */
export type RecurringDraft = {
  kind: 'expense' | 'loan'
  description: string
  category: string | null
  amountCents: number
  payers: Allocation[]
  split: Split
  note: string | null
  startOn: string
  endOn: string | null
}

/** Everything but `startOn` — which is exactly what a `PATCH` may carry. */
function recurringBody(draft: RecurringDraft) {
  return {
    kind: draft.kind,
    description: draft.description,
    category: draft.category,
    amountCents: draft.amountCents,
    ...payersBody(draft.payers, draft.amountCents),
    split: draft.split,
    note: draft.note,
    endOn: draft.endOn,
  }
}

const recurringPath = (groupId: string) => `/api/groups/${encodeURIComponent(groupId)}/recurring`

/** A group's live rules, newest first. Cancelled ones are not in it. */
export const fetchRecurring = (groupId: string, fetchImpl?: typeof fetch) =>
  request<RecurringResponse>(recurringPath(groupId), {}, fetchImpl)

/** One rule, with what it has already done — asked for only when a row is opened. */
export const fetchRecurringRule = (
  groupId: string,
  recurringId: string,
  fetchImpl?: typeof fetch,
) =>
  request<RecurringRuleResponse>(
    `${recurringPath(groupId)}/${encodeURIComponent(recurringId)}`,
    {},
    fetchImpl,
  )

export function createRecurring(
  groupId: string,
  draft: RecurringDraft,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<SavedRecurringResponse> {
  return request<SavedRecurringResponse>(
    recurringPath(groupId),
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ ...recurringBody(draft), startOn: draft.startOn }),
    },
    fetchImpl,
  )
}

/**
 * Corrects one. Like an expense's `PATCH`, the whole of the money goes every
 * time — a new amount with the old percentages is a different set of cents —
 * and nothing already materialised is touched: those are ordinary
 * transactions by now, correctable where every other expense is.
 */
export function updateRecurring(
  groupId: string,
  recurringId: string,
  draft: RecurringDraft,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<SavedRecurringResponse> {
  return request<SavedRecurringResponse>(
    `${recurringPath(groupId)}/${encodeURIComponent(recurringId)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(recurringBody(draft)),
    },
    fetchImpl,
  )
}

/** Cancels the series. Soft: it stops advancing, and what it already made stands. */
export function cancelRecurring(
  groupId: string,
  recurringId: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<SavedRecurringResponse> {
  return request<SavedRecurringResponse>(
    `${recurringPath(groupId)}/${encodeURIComponent(recurringId)}`,
    { method: 'DELETE', headers: { 'Idempotency-Key': idempotencyKey } },
    fetchImpl,
  )
}

/**
 * "Not this month."
 *
 * `occursOn` is optional to the API and always sent by this app, because it
 * is the date the screen was showing when the button was pressed. A rule has
 * exactly one date that has not happened yet, and the scheduled Worker may
 * have moved it since this list was fetched — so naming the date turns a
 * stale screen into an `occurs_on_mismatch` refusal naming the real one,
 * rather than into next month being quietly skipped instead of this one.
 */
export function skipRecurring(
  groupId: string,
  recurringId: string,
  occursOn: string,
  idempotencyKey: string,
  fetchImpl?: typeof fetch,
): Promise<SkippedRecurringResponse> {
  return request<SkippedRecurringResponse>(
    `${recurringPath(groupId)}/${encodeURIComponent(recurringId)}/skip`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ occursOn }),
    },
    fetchImpl,
  )
}

// ---------------------------------------------------------------------------
// The admin console
// ---------------------------------------------------------------------------

/**
 * The console's two reads, and the shapes they answer with.
 *
 * There is no money in either of them, and that is not something this file
 * enforces — `server/admin.ts` is where it is structural, and
 * `tests/admin.test.ts` walks every one of that module's responses to keep it
 * so. What these types do is make the absence visible from the client side
 * too: there is no field here a screen could render an amount out of, so an
 * admin screen that wanted to show one would have to change the server first.
 */

/** One effective limit, and where it came from. Same shape as `Limit`. */
export type AdminLimit = Limit

/**
 * What an account *is*, in the console's own four words.
 *
 * `status` below is the column — `active`, `suspended`, `deactivated` — and
 * this is the same fact said the way an admin thinks about it. They are not
 * the same list because a ban and a disable share a column value and differ by
 * a row in the blocklist; the server works that out, once, and sends the
 * answer. See `server/admin.ts`.
 */
export type AccountState = 'active' | 'disabled' | 'banned' | 'deleted'

export type AdminUser = {
  id: UserId
  displayName: string
  email: string
  isAdmin: boolean
  /** Somebody a member typed a name for; they cannot sign in on their own. */
  isPlaceholder: boolean
  status: string
  /** The same fact as `status`, with a ban told apart from a disable. */
  state: AccountState
  statusReason: string | null
  statusChangedAt: string | null
  createdAt: string
  /** The last request from any of their browsers, or `null` for never. */
  lastSeenAt: string | null
  /** The plan in force. Nothing in the SPA branches on it either. */
  planId: string | null
  /** The effective ceiling, with `source` saying whether an admin set it. */
  groupLimit: AdminLimit
  /** The attachment switch, read the way the server reads it. */
  uploads: AdminLimit & { allowed: boolean }
  /** Every key an admin has in force on this account. */
  overrides: (AdminLimit & { limitKey: string })[]
  /** Groups they are currently in — a count, never a name. */
  groups: number
  /** Files they have uploaded — a count, never a name, a key or a URL. */
  files: number
  storageBytesUsed: number
}

/** One row of `user_limit_overrides` — the number, and who wrote it down. */
export type AdminOverride = {
  limitKey: string
  value: number | null
  unlimited: boolean
  reason: string | null
  setBy: string | null
  setByName: string | null
  setAt: string
  expiresAt: string | null
  /** Whether the resolver is using it, or it has lapsed and been left in place. */
  live: boolean
}

/** One thing an admin did to this account. No amount could ever be in it. */
export type AdminAuditEntry = {
  id: number
  action: string
  by: string | null
  byName: string | null
  at: string
  key: string | null
  before: unknown
  after: unknown
  reason: string | null
}

/** What a ban put in the blocklist: the kind of identifier, never its value. */
export type AdminBlock = { type: string; reason: string | null; since: string }

/**
 * One account, as the single-account view reads it: everything the table shows
 * and the three things only this view needs — why each override was set, what
 * is blocked, and what has been done to the account before.
 */
export type AdminUserDetail = AdminUser & {
  limitOverrides: AdminOverride[]
  /** Every key this deployment can be given an override for. The server's list. */
  limitKeys: string[]
  blocks: AdminBlock[]
  audit: AdminAuditEntry[]
}

/** Every write answers with the account afterwards, so a screen replaces it. */
export type AdminUserResponse = {
  status: 'ok'
  user: AdminUserDetail
  /** What a delete took out of R2. A count and a size, and only on a delete. */
  erased?: { files: number; bytes: number } | null
}

export type AdminUsersResponse = {
  status: 'ok'
  users: AdminUser[]
  /** How many match this filter at all. First page only, like every total here. */
  matching: number | null
  nextCursor: string | null
}

export type AdminStats = {
  status: 'ok'
  people: {
    total: number
    active: number
    suspended: number
    deactivated: number
    admins: number
    placeholders: number
  }
  plans: { planId: string; name: string; people: number }[]
  overrides: { live: number; people: number }
  groups: { total: number; live: number }
  storage: { files: number; bytes: number }
  seen: { week: number; month: number; never: number }
}

/** What the table is filtered by. Empty strings mean "do not narrow". */
export type AdminUserQuery = {
  q?: string
  status?: string
  plan?: string
  overrides?: string
  cursor?: string | null
}

export const fetchAdminStats = (fetchImpl?: typeof fetch) =>
  request<AdminStats>('/api/admin/stats', {}, fetchImpl)

/**
 * A page of accounts. Every filter is left out when it is not narrowing
 * anything, so the URL a screen with no filters on asks for is exactly the one
 * an unfiltered read should be cached under.
 */
export function fetchAdminUsers(
  query: AdminUserQuery = {},
  fetchImpl?: typeof fetch,
): Promise<AdminUsersResponse> {
  const search = new URLSearchParams()
  for (const key of ['q', 'status', 'plan', 'overrides'] as const) {
    const value = query[key]?.trim()
    if (value) search.set(key, value)
  }
  if (query.cursor) search.set('cursor', query.cursor)

  const suffix = search.toString()
  return request<AdminUsersResponse>(
    `/api/admin/users${suffix ? `?${suffix}` : ''}`,
    {},
    fetchImpl,
  )
}

/**
 * The console's writes.
 *
 * There is no `Idempotency-Key` on any of them, unlike every other write in
 * this file, and `server/admin.ts` says why: each one sets something to a
 * value rather than creating anything, so a retry converges instead of
 * happening twice. Every one of them carries a `reason`, because the server
 * refuses without one — an audit trail whose reasons are blank is a list of
 * things that happened rather than a record anybody can act on later.
 *
 * They all answer with the whole account afterwards, so a screen shows the
 * server's version of what it just did rather than its own guess at it.
 */
function adminWrite(
  method: 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  body: unknown,
  fetchImpl?: typeof fetch,
): Promise<AdminUserResponse> {
  return request<AdminUserResponse>(
    path,
    { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) },
    fetchImpl,
  )
}

const adminUserPath = (userId: string) => `/api/admin/users/${encodeURIComponent(userId)}`

export const fetchAdminUser = (userId: string, fetchImpl?: typeof fetch) =>
  request<AdminUserResponse>(adminUserPath(userId), {}, fetchImpl)

/** `planId: null` is the default plan, which is what almost everybody is on. */
export const setAdminPlan = (
  userId: string,
  patch: { planId: string | null; reason: string },
  fetchImpl?: typeof fetch,
) => adminWrite('PUT', `${adminUserPath(userId)}/plan`, patch, fetchImpl)

/**
 * One override. `value: null` is unlimited — never zero — and the attachment
 * switch is this same call with `1` or `0`, because the switch is a limit like
 * any other and a second call for it would be a second way to write one.
 */
export const setAdminLimit = (
  userId: string,
  limitKey: string,
  patch: { value: number | null; reason: string; expiresAt?: string | null },
  fetchImpl?: typeof fetch,
) =>
  adminWrite(
    'PUT',
    `${adminUserPath(userId)}/limits/${encodeURIComponent(limitKey)}`,
    patch,
    fetchImpl,
  )

export const clearAdminLimit = (
  userId: string,
  limitKey: string,
  patch: { reason: string },
  fetchImpl?: typeof fetch,
) =>
  adminWrite(
    'DELETE',
    `${adminUserPath(userId)}/limits/${encodeURIComponent(limitKey)}`,
    patch,
    fetchImpl,
  )

export const setAdminState = (
  userId: string,
  patch: { state: AccountState; reason: string },
  fetchImpl?: typeof fetch,
) => adminWrite('PATCH', `${adminUserPath(userId)}/status`, patch, fetchImpl)
